import React, { useEffect, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import SubscriptionPaymentForm from './SubscriptionPaymentForm';
import subscriptionApi from '@/api/subscriptionApi';
import organisationApi from '@/api/organisationApi';
import ModalNoClose from "@/components/modal/ModalNoClose.tsx";
import { useTranslation } from 'react-i18next';

interface StripeCustomerSetupResponse {
    clientSecret: string;
    customerId: string;
}

interface Invoice {
    id: number;
    organisationID: number;
    created: string;
    dueDate: string;
    paymentProvider: string;
    paymentProviderID: string;
    paymentProviderRequest?: {
        Currency: string;
        CustomerNumber: string;
        DueDate: string;
        InvoiceDate: string;
        InvoiceRows: Array<{
            AccountNumber: number;
            DeliveredQuantity: number;
            Description: string;
            Price: number;
            VAT: number;
        }>;
        Language: string;
        Remarks?: string;
    };
    paymentProviderResponse?: {
        Invoice: {
            Currency: string;
            CustomerNumber: string;
            DocumentNumber: string;
            DueDate: string;
            InvoiceDate: string;
            InvoiceRows: Array<{
                AccountNumber: number;
                DeliveredQuantity: number;
                Description: string;
                DiscountType: string;
                Price: number;
                VAT: number;
            }>;
            InvoiceType: string;
            Language: string;
            Net: number;
            PaymentTerms: string;
            Total: number;
            VAT: number;
        };
    };
    summary?: {
        addonCharges?: number;
        levelCharges?: number;
        count?: number;
        message?: string;
    };
    paid?: boolean;
    status?: string;
}

interface SubscriptionCheckModalProps {
    isOpen: boolean;
    onClose: () => void;
    paymentActive: boolean;
    paymentProvider: 'STRIPE' | 'FORTNOX';
}

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE);

const SubscriptionCheckModal: React.FC<SubscriptionCheckModalProps> = ({
    isOpen,
    onClose,
    paymentActive,
    paymentProvider
}) => {
    const { t } = useTranslation();
    const [setupIntent, setSetupIntent] = useState<StripeCustomerSetupResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [unpaidInvoices, setUnpaidInvoices] = useState<Invoice[]>([]);
    const [, setLoadingInvoices] = useState(false);

    // Fetch unpaid invoices when modal opens
    useEffect(() => {
        const fetchUnpaidInvoices = async () => {
            if (!isOpen || paymentProvider !== 'STRIPE' || paymentActive) {
                return;
            }

            setLoadingInvoices(true);
            try {
                // Check subscription endpoint for unpaid invoices
                const subscriptionResponse = await organisationApi.getSubscription();
                const subscriptionData = subscriptionResponse.data;

                // Check if subscription data has unpaid invoices
                let unpaid: Invoice[] = [];
                
                if (subscriptionData?.unpaidInvoices && Array.isArray(subscriptionData.unpaidInvoices)) {
                    unpaid = subscriptionData.unpaidInvoices;
                } else {
                    // Fallback: fetch all invoices and filter for unpaid ones
                    try {
                        const invoicesResponse = await organisationApi.getInvoices();
                        const allInvoices: Invoice[] = invoicesResponse.data || [];
                        // Filter for unpaid invoices (not paid and due date exists)
                        unpaid = allInvoices.filter((invoice: Invoice) => {
                            const isPaid = invoice.paid === true || invoice.status === 'paid' || invoice.status === 'PAID';
                            const hasDueDate = invoice.dueDate;
                            return !isPaid && hasDueDate;
                        });
                    } catch (invoiceError) {
                        console.error('Error fetching invoices:', invoiceError);
                        // Continue without showing invoices if this fails
                    }
                }

                setUnpaidInvoices(unpaid);
            } catch (error) {
                console.error('Error fetching subscription data:', error);
                // Don't show error to user, just log it
                setUnpaidInvoices([]);
            } finally {
                setLoadingInvoices(false);
            }
        };

        if (isOpen) {
            fetchUnpaidInvoices();
        }
    }, [isOpen, paymentProvider, paymentActive]);

    useEffect(() => {
        const setupStripeCustomer = async () => {
            if (isLoading || setupIntent) {
                return; // Prevent multiple calls
            }
            
            setIsLoading(true);
            try {
                console.log('Creating Stripe customer...');
                const response = await subscriptionApi.setupStripeCustomer();
                setSetupIntent(response);
                setError(null);
                console.log('Stripe customer created:', response);
            } catch (error) {
                console.error('Error setting up Stripe customer:', error);
                setError('Failed to initialize payment setup. Please try again.');
            } finally {
                setIsLoading(false);
            }
        };

        if (paymentProvider === 'STRIPE' && !paymentActive && isOpen) {
            setupStripeCustomer();
        }
    }, [paymentProvider, paymentActive, isOpen, isLoading, setupIntent]);

    const handleSetupComplete = async () => {
        try {
            onClose();
        } catch (error) {
            console.error('Error completing setup:', error);
            setError('Failed to complete setup. Please try again.');
        }
    };

    // Reset state when modal closes
    useEffect(() => {
        if (!isOpen) {
            setSetupIntent(null);
            setError(null);
            setIsLoading(false);
            setUnpaidInvoices([]);
        }
    }, [isOpen]);

    const renderContent = () => {
        if (error) {
            return (
                <div className="text-center p-6">
                    <h3 className="text-xl font-bold mb-4 text-red-600">Error</h3>
                    <p className="text-red-500">{error}</p>
                </div>
            );
        }

        if (isLoading) {
            return (
                <div className="text-center p-6">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <h3 className="text-xl font-bold mb-2">Setting up payment...</h3>
                    <p className="text-gray-600">Please wait while we prepare your payment setup.</p>
                </div>
            );
        }

        if (!paymentActive && paymentProvider === 'STRIPE' && setupIntent) {
            return (
                <div className="p-6">
                    <Elements 
                        stripe={stripePromise} 
                        options={{
                            clientSecret: setupIntent.clientSecret,
                            appearance: {
                                theme: 'stripe',
                            },
                        }}
                    >
                        <SubscriptionPaymentForm 
                            onSetupComplete={handleSetupComplete}
                            customerId={setupIntent.customerId}
                            clientSecret={setupIntent.clientSecret}
                        />
                    </Elements>
                    
                    {/* Unpaid Invoices Section */}
                    {unpaidInvoices.length > 0 && (
                        <div className="mt-6 pt-6 border-t border-gray-200">
                            <h4 className="text-lg font-semibold mb-3 text-gray-800">
                                {t('subscription.setup.unpaidInvoices.title', 'Unpaid Invoices')}
                            </h4>
                            <p className="text-sm text-gray-600 mb-4">
                                {t('subscription.setup.unpaidInvoices.message', 'The following invoices will be paid automatically when you set up your payment method:')}
                            </p>
                            <div className="space-y-3">
                                {unpaidInvoices.map((invoice) => {
                                    const invoiceNumber = invoice.paymentProviderResponse?.Invoice?.DocumentNumber || 
                                                         invoice.paymentProviderID || 
                                                         `#${invoice.id}`;
                                    const amount = invoice.paymentProviderResponse?.Invoice?.Total || 0;
                                    const currency = invoice.paymentProviderResponse?.Invoice?.Currency || '';
                                    const dueDate = invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : '-';
                                    const isOverdue = invoice.dueDate ? new Date(invoice.dueDate) < new Date() : false;
                                    
                                    return (
                                        <div 
                                            key={invoice.id} 
                                            className={`p-4 rounded-lg border ${
                                                isOverdue 
                                                    ? 'bg-red-50 border-red-200' 
                                                    : 'bg-yellow-50 border-yellow-200'
                                            }`}
                                        >
                                            <div className="flex justify-between items-start">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className="font-medium text-gray-900">
                                                            {invoiceNumber}
                                                        </span>
                                                        {isOverdue && (
                                                            <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-800 rounded">
                                                                {t('subscription.invoices.status.overdue', 'Overdue')}
                                                            </span>
                                                        )}
                                                        {!isOverdue && (
                                                            <span className="px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800 rounded">
                                                                {t('subscription.invoices.status.pending', 'Pending')}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="text-sm text-gray-600">
                                                        <span>{t('subscription.invoices.table.headers.dueDate', 'Due Date')}: {dueDate}</span>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <div className="font-semibold text-gray-900">
                                                        {currency} {amount.toFixed(2)}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            );
        }
    };

    if (!isOpen) return null;

    return (
        <ModalNoClose>
            {renderContent()}
        </ModalNoClose>
    );
};

export default SubscriptionCheckModal; 